export interface LogEntry {
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  source: string;
  properties?: Record<string, any>;
}

export interface LogBatch {
  logs: LogEntry[];
  accountId: string;
}

class Logger {
  private logQueue: LogEntry[] = [];
  private batchSize = 10;
  private flushInterval = 5000; // 5 seconds
  private flushTimer: NodeJS.Timeout | null = null;
  private accountId: string | null = null;

  constructor() {
    this.startFlushTimer();
  }

  setAccountId(accountId: string) {
    this.accountId = accountId;
  }

  private startFlushTimer() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }

  private async sendLogs(logs: LogEntry[]): Promise<void> {
    if (!this.accountId) {
      console.warn('Logger: Account ID not set, cannot send logs');
      return;
    }

    if (logs.length === 0) {
      return;
    }

    const logBatch: LogBatch = {
      logs,
      accountId: this.accountId
    };

    try {
      const response = await fetch('https://visitorjourneyapi.dev.trueengage.com/Visitor/Log', {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logBatch),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log(`Logger: Successfully sent ${logs.length} logs`);
    } catch (error) {
      console.error('Logger: Error sending logs:', error);
      // Re-add logs to queue for retry
      this.logQueue.unshift(...logs);
    }
  }

  private async flush(): Promise<void> {
    if (this.logQueue.length === 0) {
      return;
    }

    const logsToSend = this.logQueue.splice(0, this.batchSize);
    await this.sendLogs(logsToSend);
  }

  private addLog(level: LogEntry['level'], message: string, source: string, properties?: Record<string, any>): void {
    const logEntry: LogEntry = {
      level,
      message,
      source,
      properties: {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        ...properties
      }
    };

    this.logQueue.push(logEntry);

    // Auto-flush if queue is full
    if (this.logQueue.length >= this.batchSize) {
      this.flush();
    }
  }

  info(message: string, source: string, properties?: Record<string, any>): void {
    this.addLog('info', message, source, properties);
  }

  warn(message: string, source: string, properties?: Record<string, any>): void {
    this.addLog('warn', message, source, properties);
  }

  error(message: string, source: string, properties?: Record<string, any>): void {
    this.addLog('error', message, source, properties);
  }

  debug(message: string, source: string, properties?: Record<string, any>): void {
    this.addLog('debug', message, source, properties);
  }

  // Force flush all pending logs
  async forceFlush(): Promise<void> {
    await this.flush();
  }

  // Cleanup
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    // Send remaining logs before destroying
    this.forceFlush();
  }
}

// Create singleton instance
export const logger = new Logger();

// Auto-cleanup on page unload
window.addEventListener('beforeunload', () => {
  logger.forceFlush();
});
